import styled, { css } from 'styled-components';

export type ActionButtonVariant = 'primary' | 'secondary' | 'danger' | 'default' | 'ghost';

interface ActionButtonProps {
  $variant?: ActionButtonVariant;
  variant?: ActionButtonVariant;
  $size?: 'sm' | 'md';
}

const primaryStyles = css`
  background: linear-gradient(135deg, #1a4d4d 0%, #0f3333 100%);
  color: #fff;
  border: none;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(224, 123, 45, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const secondaryStyles = css`
  background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#ffffff')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#6b7280')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#dcdfe4')};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    background: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#f3f4f6')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#374151')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const dangerStyles = css`
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#dc2626')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.3)' : '#fee2e2')};

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.25)' : '#fee2e2')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ghostStyles = css`
  background: transparent;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  border: none;

  &:hover:not(:disabled) {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#111827')};
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const getVariantStyles = (variant?: ActionButtonVariant) => {
  switch (variant) {
    case 'primary':
      return primaryStyles;
    case 'danger':
      return dangerStyles;
    case 'ghost':
      return ghostStyles;
    case 'secondary':
    case 'default':
    default:
      return secondaryStyles;
  }
};

export const ActionButton = styled.button<ActionButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${({ $size }) => ($size === 'sm' ? '6px 10px' : '8px 14px')};
  border-radius: 6px;
  font-size: ${({ $size }) => ($size === 'sm' ? '12px' : '13px')};
  font-weight: 300;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $variant, variant }) => getVariantStyles($variant || variant)}
`;

export const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const IconActionButton = styled.button<ActionButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ $variant, variant }) => getVariantStyles($variant || variant)}
`;
